import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { CheckIcon, ShieldCheckIcon, BrainIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-8 pb-12 md:pt-20 md:pb-32 px-4 md:px-6 overflow-hidden" data-id="element-157">
      {/* Base background gradient: deep navy */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F1D32] via-[#0A1628] to-[#0A1628] -z-20" data-id="element-158" />

      {/* Primary light beam — blue glow from top center */}
      <div className="absolute -z-10 pointer-events-none" style={{
      top: '-10%',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60vw',
      height: '110%',
      background: 'radial-gradient(ellipse 50% 80% at 50% 0%, rgba(59,130,246,0.3) 0%, rgba(30,58,95,0.4) 30%, rgba(10,22,40,0.1) 65%, transparent 100%)',
      filter: 'blur(2px)'
    }} data-id="element-159" />

      {/* Secondary wide ambient glow */}
      <div className="absolute -z-10 pointer-events-none" style={{
      top: '-5%',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100vw',
      height: '80%',
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(59,130,246,0.15) 0%, rgba(30,58,95,0.08) 50%, transparent 100%)'
    }} data-id="element-160" />

      {/* Bright core — subtle white/blue glow at top */}
      <div className="absolute -z-10 pointer-events-none" style={{
      top: '-20px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '40vw',
      height: '200px',
      background: 'radial-gradient(ellipse 60% 100% at 50% 0%, rgba(255,255,255,0.12) 0%, rgba(59,130,246,0.15) 40%, transparent 100%)',
      filter: 'blur(8px)'
    }} data-id="element-161" />

      {/* Content */}
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-162">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-163">
          <div className="flex-1 text-center lg:text-left z-10" data-id="element-164">
            <FadeIn data-id="element-165">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold font-semibold text-xs md:text-sm mb-6 uppercase tracking-wider" data-id="element-166">
                <span data-id="element-167">🌙</span>
                <span data-id="element-168">Para mamás que ya lo han intentado todo</span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-169">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-black leading-[1.15] mb-6 text-white text-balance" data-id="element-170">
                Ayuda a tu bebé a dormir <br className="hidden md:block" data-id="element-171" />
                <span className="text-gold italic block mt-2" data-id="element-172">
                  noches completas
                </span>
                <span className="text-3xl md:text-4xl lg:text-5xl mt-2 block font-normal" data-id="element-173">
                  sin llanto extremo
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-174">
              <p className="text-lg md:text-xl text-white/75 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-175">
                Una guía paso a paso con base científica para entender los
                ciclos de sueño, aplicar ventanas de sueño y recuperar tu
                descanso. Para bebés de 0 a 3 años.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-176">
              <ul className="flex flex-col gap-3 mb-10 text-left max-w-xl mx-auto lg:mx-0" data-id="element-177">
                {['Ventanas de sueño por edad + tablas listas para aplicar hoy', 'Rutinas efectivas desde recién nacido hasta 3 años', 'Guía de regresiones (4, 8 y 12 meses) sin perder lo avanzado', 'Enfoque respetuoso: sin métodos de llanto extremo'].map((bullet, i) => <li key={i} className="flex items-start gap-3 text-white/85 text-sm md:text-base" data-id="element-178">
                    <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center" data-id="element-179">
                      <CheckIcon className="w-3.5 h-3.5 text-gold" data-id="element-180" />
                    </div>
                    <span data-id="element-181">{bullet}</span>
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.4} className="flex flex-col items-center lg:items-start gap-4" data-id="element-182">
              <Button href="#pricing" className="w-full sm:w-auto px-8 py-4 text-lg animate-[pulse_2.5s_ease-in-out_infinite]" data-id="element-183">
                🌙 Quiero Dormir Mejor Hoy
              </Button>
              <div className="flex items-center gap-2 text-xs md:text-sm text-white/55" data-id="element-184">
                <span data-id="element-185">🔒</span>
                <span data-id="element-186">
                  Garantía de devolución 7 días · Acceso inmediato · Sin riesgo
                </span>
              </div>
            </FadeIn>
          </div>

          <div className="flex-1 w-full max-w-md lg:max-w-none relative mt-10 lg:mt-0" data-id="element-187">
            <FadeIn delay={0.3} direction="left" data-id="element-188">
              <div className="relative z-10" data-id="element-189">
                {/* Real Mockup Photo */}
                <div className="rounded-3xl overflow-hidden shadow-2xl border-2 border-gold/20" data-id="element-190">
                  <img src="https://cdn.magicpatterns.com/uploads/v5CgucnhHjRwbf7Ns2cbnx/heylloyd-image-1772909402894.png" alt="Mamá descansando con bebé dormido mientras lee la Guía del Sueño del Bebé en tablet" className="w-full h-auto" data-id="element-191" />
                </div>

                {/* Floating Badges */}
                <div className="absolute -top-5 -right-5 md:-right-10 bg-white rounded-xl p-3 md:p-4 shadow-xl flex items-center gap-3 z-20 animate-[float_4s_ease-in-out_infinite]" data-id="element-192">
                  <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center" data-id="element-193">
                    <ShieldCheckIcon className="w-5 h-5 text-gold-dark" data-id="element-194" />
                  </div>
                  <div data-id="element-195">
                    <div className="text-xs md:text-sm font-bold text-navy" data-id="element-196">
                      Garantía 7 días
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500" data-id="element-197">
                      Sin preguntas
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-5 -left-5 md:-left-10 bg-white rounded-xl p-3 md:p-4 shadow-xl flex items-center gap-3 z-20 animate-[float_4s_ease-in-out_infinite_reverse]" data-id="element-198">
                  <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center" data-id="element-199">
                    <BrainIcon className="w-5 h-5 text-navy" data-id="element-200" />
                  </div>
                  <div data-id="element-201">
                    <div className="text-xs md:text-sm font-bold text-navy" data-id="element-202">
                      Base científica
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500" data-id="element-203">
                      Apego seguro
                    </div>
                  </div>
                </div>
              </div>

              {/* Background glow */}
              <div className="absolute -inset-10 bg-blue-500/20 blur-3xl -z-10 rounded-full opacity-50" data-id="element-204" />
            </FadeIn>
          </div>
        </div>
      </div>

      {/* Bottom fade to navy */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#0A1628] -z-10 pointer-events-none" data-id="element-205" />
    </section>;
}